import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { AuthPayload, EmailVerificationRequest, PublicConfig, PublicUser, SetupInstallPayload, SetupStatus, UnreadState, UserStatus } from "../../shared/contracts";
import { api, jsonBody } from "../lib/api";

interface SessionUser extends PublicUser {
  account: string;
  status: UserStatus;
  email: string | null;
  emailVerified: boolean;
  hasPassword: boolean;
  qqBound: boolean;
  onboardingRequired: boolean;
  postTransferEnabled: boolean;
}

type ConnectionState = "offline" | "connecting" | "online" | "reconnecting";
interface LoginPromptState { message: string; nextPath: string }

interface AppContextValue {
  setupStatus: SetupStatus | null;
  setupLoading: boolean;
  setupError: string | null;
  config: PublicConfig | null;
  configLoading: boolean;
  user: SessionUser | null;
  authLoading: boolean;
  unread: UnreadState;
  connectionState: ConnectionState;
  loginPrompt: LoginPromptState | null;
  sessionReplaced: boolean;
  requestLogin(message: string, nextPath: string): void;
  dismissLoginPrompt(): void;
  acknowledgeSessionReplaced(): void;
  refreshConfig(): Promise<void>;
  completeSetup(account: string, nickname: string, password: string): Promise<void>;
  refreshSetup(): Promise<void>;
  login(identifier: string, password: string): Promise<void>;
  register(account: string, password: string, nickname: string): Promise<void>;
  requestEmailRegistration(account: string, password: string, nickname: string, email: string): Promise<EmailVerificationRequest>;
  confirmEmailRegistration(verificationId: string, code: string): Promise<void>;
  requestEmailBinding(email: string): Promise<EmailVerificationRequest>;
  confirmEmailBinding(verificationId: string, code: string): Promise<void>;
  requestPasswordReset(email: string): Promise<EmailVerificationRequest>;
  confirmPasswordReset(verificationId: string, code: string, newPassword: string): Promise<void>;
  exchangeQqTicket(ticket: string): Promise<SessionUser>;
  logout(): void;
  refreshUnread(): Promise<void>;
  refreshUser(): Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);
const emptyUnread: UnreadState = { chat: 0, groups: 0, activity: 0, total: 0 };

export function AppProvider({ children }: { children: ReactNode }) {
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null);
  const [setupLoading, setSetupLoading] = useState(true);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [unread, setUnread] = useState<UnreadState>(emptyUnread);
  const [connectionState, setConnectionState] = useState<ConnectionState>("offline");
  const [loginPrompt, setLoginPrompt] = useState<LoginPromptState | null>(null);
  const [sessionReplaced, setSessionReplaced] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const sessionReplacedRef = useRef(false);

  const acceptAuthenticatedUser = useCallback((nextUser: SessionUser) => {
    sessionReplacedRef.current = false;
    setSessionReplaced(false);
    setUser(nextUser);
  }, []);

  const handleSessionReplaced = useCallback(() => {
    sessionReplacedRef.current = true;
    if (reconnectTimer.current) window.clearTimeout(reconnectTimer.current);
    reconnectTimer.current = null;
    setConnectionState("offline");
    setUnread(emptyUnread);
    setLoginPrompt(null);
    setUser(null);
    setSessionReplaced(true);
  }, []);

  const refreshUnread = useCallback(async () => {
    try { setUnread(await api<UnreadState>("/chat/unread")); }
    catch { setUnread(emptyUnread); }
  }, []);

  const refreshUser = useCallback(async () => {
    try { setUser((await api<{ user: SessionUser }>("/auth/me")).user); }
    catch { setUser(null); }
    finally { setAuthLoading(false); }
  }, []);

  const loadPublicConfig = useCallback(async () => {
    setConfigLoading(true);
    try { setConfig(await api<PublicConfig>("/config")); }
    finally { setConfigLoading(false); }
  }, []);

  const refreshSetup = useCallback(async () => {
    setSetupLoading(true);
    setSetupError(null);
    try {
      const status = await api<SetupStatus>("/setup/status");
      setSetupStatus(status);
      if (!status.installed) {
        setUser(null); setUnread(emptyUnread); setConfig(null); setConfigLoading(false); setAuthLoading(false);
        return;
      }
      await Promise.all([loadPublicConfig(), refreshUser()]);
    } catch (error) {
      setSetupStatus(null);
      setSetupError(error instanceof Error ? error.message : "无法确认平台安装状态");
      setConfigLoading(false); setAuthLoading(false);
    } finally { setSetupLoading(false); }
  }, [loadPublicConfig, refreshUser]);

  useEffect(() => {
    window.addEventListener("share-moments:session-replaced", handleSessionReplaced);
    return () => window.removeEventListener("share-moments:session-replaced", handleSessionReplaced);
  }, [handleSessionReplaced]);

  useEffect(() => { void refreshSetup(); }, [refreshSetup]);
  useEffect(() => {
    if (!config?.site) return;
    document.title = `${config.site.name} · 游戏账号实拍社区`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", config.site.description);

    const iconHref = config.site.avatarUrl ?? "/favicon.ico";
    const syncIconLink = (id: string, rel: string, sizes?: string) => {
      let link = document.getElementById(id) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.id = id;
        link.rel = rel;
        document.head.append(link);
      }
      link.setAttribute("href", iconHref);
      if (sizes) link.setAttribute("sizes", sizes);
    };
    syncIconLink("site-favicon", "icon");
    syncIconLink("site-apple-touch-icon", "apple-touch-icon", "180x180");
  }, [config?.site]);

  const completeSetup = useCallback(async (account: string, nickname: string, password: string) => {
    const result = await api<SetupInstallPayload>("/setup/install", { method: "POST", ...jsonBody({ account, nickname, password }) });
    acceptAuthenticatedUser(result.user as SessionUser); setAuthLoading(false); setSetupError(null);
    await loadPublicConfig().catch(() => setConfig(null));
    setSetupStatus({ installed: true });
  }, [acceptAuthenticatedUser, loadPublicConfig]);

  useEffect(() => {
    if (!setupStatus?.installed) return;
    const handler = () => void refreshSetup();
    window.addEventListener("share-moments:setup-required", handler);
    return () => window.removeEventListener("share-moments:setup-required", handler);
  }, [refreshSetup, setupStatus?.installed]);

  useEffect(() => {
    const handler = () => { setUser(null); setUnread(emptyUnread); };
    window.addEventListener("share-moments:unauthorized", handler);
    return () => window.removeEventListener("share-moments:unauthorized", handler);
  }, []);

  useEffect(() => {
    if (!user) { setConnectionState("offline"); return; }
    let disposed = false;
    let attempts = 0;
    const connect = () => {
      if (disposed || !navigator.onLine) { setConnectionState("offline"); return; }
      setConnectionState(attempts ? "reconnecting" : "connecting");
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(`${protocol}//${window.location.host}/ws`);
      socketRef.current = socket;
      socket.addEventListener("open", () => { attempts = 0; setConnectionState("online"); void refreshUnread(); });
      socket.addEventListener("message", (event) => {
        if (event.data === "pong") return;
        try {
          const payload = JSON.parse(String(event.data)) as { event: string; data: unknown };
          if (payload.event === "session.replaced") {
            handleSessionReplaced();
            return;
          }
          window.dispatchEvent(new CustomEvent(`share-moments:${payload.event}`, { detail: payload.data }));
          if (payload.event === "unread.updated") setUnread(payload.data as UnreadState);
          if (payload.event === "account.updated") void refreshUser();
        } catch { return; }
      });
      socket.addEventListener("close", (event) => {
        if (disposed) return;
        if (event.code === 4004 || sessionReplacedRef.current) {
          handleSessionReplaced();
          return;
        }
        attempts += 1;
        setConnectionState(navigator.onLine ? "reconnecting" : "offline");
        reconnectTimer.current = window.setTimeout(connect, Math.min(1000 * 2 ** attempts, 15_000));
      });
    };
    const online = () => { if (!socketRef.current || socketRef.current.readyState > WebSocket.OPEN) connect(); };
    const offline = () => { setConnectionState("offline"); socketRef.current?.close(); };
    window.addEventListener("online", online); window.addEventListener("offline", offline);
    connect();
    return () => {
      disposed = true;
      window.removeEventListener("online", online); window.removeEventListener("offline", offline);
      if (reconnectTimer.current) window.clearTimeout(reconnectTimer.current);
      socketRef.current?.close();
    };
  }, [handleSessionReplaced, refreshUnread, refreshUser, user?.id]);

  const login = useCallback(async (identifier: string, password: string) => {
    const result = await api<AuthPayload>("/auth/login", { method: "POST", ...jsonBody({ identifier, password }) });
    acceptAuthenticatedUser(result.user as SessionUser); await refreshUnread();
  }, [acceptAuthenticatedUser, refreshUnread]);

  const register = useCallback(async (account: string, password: string, nickname: string) => {
    const result = await api<AuthPayload>("/auth/register", { method: "POST", ...jsonBody({ account, password, nickname }) });
    acceptAuthenticatedUser(result.user as SessionUser);
  }, [acceptAuthenticatedUser]);

  const requestEmailRegistration = useCallback((account: string, password: string, nickname: string, email: string) => (
    api<EmailVerificationRequest>("/auth/register/request", { method: "POST", ...jsonBody({ account, password, nickname, email }) })
  ), []);

  const confirmEmailRegistration = useCallback(async (verificationId: string, code: string) => {
    const result = await api<AuthPayload>("/auth/register/confirm", { method: "POST", ...jsonBody({ verificationId, code }) });
    acceptAuthenticatedUser(result.user as SessionUser);
  }, [acceptAuthenticatedUser]);

  const requestEmailBinding = useCallback((email: string) => (
    api<EmailVerificationRequest>("/auth/email/request", { method: "POST", ...jsonBody({ email }) })
  ), []);

  const confirmEmailBinding = useCallback(async (verificationId: string, code: string) => {
    const result = await api<{ user: SessionUser }>("/auth/email/confirm", { method: "POST", ...jsonBody({ verificationId, code }) });
    acceptAuthenticatedUser(result.user);
  }, [acceptAuthenticatedUser]);

  const requestPasswordReset = useCallback((email: string) => (
    api<EmailVerificationRequest>("/auth/password/reset/request", { method: "POST", ...jsonBody({ email }) })
  ), []);

  const confirmPasswordReset = useCallback(async (verificationId: string, code: string, newPassword: string) => {
    await api("/auth/password/reset/confirm", { method: "POST", ...jsonBody({ verificationId, code, newPassword }) });
  }, []);

  const exchangeQqTicket = useCallback(async (ticket: string) => {
    const result = await api<AuthPayload>("/auth/qq/exchange", { method: "POST", ...jsonBody({ ticket }) });
    const nextUser = result.user as SessionUser;
    acceptAuthenticatedUser(nextUser);
    await refreshUnread();
    return nextUser;
  }, [acceptAuthenticatedUser, refreshUnread]);

  const logout = useCallback(() => {
    void api("/auth/logout", { method: "POST" }).catch(() => undefined);
    sessionReplacedRef.current = false;
    setSessionReplaced(false);
    socketRef.current?.close(); setUser(null); setUnread(emptyUnread);
  }, []);

  const acknowledgeSessionReplaced = useCallback(() => {
    setSessionReplaced(false);
    void api("/auth/logout", { method: "POST" }).catch(() => undefined);
  }, []);

  const requestLogin = useCallback((message: string, nextPath: string) => {
    setLoginPrompt({ message, nextPath });
  }, []);
  const dismissLoginPrompt = useCallback(() => setLoginPrompt(null), []);

  const value = useMemo<AppContextValue>(() => ({
    setupStatus, setupLoading, setupError, config, configLoading, user, authLoading, unread, connectionState,
    loginPrompt, requestLogin, dismissLoginPrompt, sessionReplaced, acknowledgeSessionReplaced,
    refreshConfig: loadPublicConfig, completeSetup, refreshSetup, login, register,
    requestEmailRegistration, confirmEmailRegistration, requestEmailBinding, confirmEmailBinding,
    requestPasswordReset, confirmPasswordReset, exchangeQqTicket,
    logout, refreshUnread, refreshUser
  }), [acknowledgeSessionReplaced, authLoading, completeSetup, config, configLoading, connectionState, confirmEmailBinding, confirmEmailRegistration, confirmPasswordReset, dismissLoginPrompt, exchangeQqTicket, loadPublicConfig, login, loginPrompt, logout, refreshSetup, register, requestEmailBinding, requestEmailRegistration, requestLogin, requestPasswordReset, sessionReplaced, setupError, setupLoading, setupStatus, refreshUnread, refreshUser, unread, user]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used inside AppProvider");
  return context;
}
